import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Zap, Wrench, Paintbrush, FileText, Grid3x3, Calculator, Eye, Plus,
  Trash2, CheckCircle2, ArrowLeft, Download, MapPin, AlertCircle, Loader2, CheckCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { apiClient, backendUrl } from "@/lib/api";
import { getBlockNumber } from "@/lib/blockMapping";
import { getCategoryConfig, labelOf, UNIT_OPTIONS, RETENTION_OPTIONS, HOME_TYPES } from "@/lib/formConfigs";
import { CURRENCY_OPTIONS, fmtMoney, cleanNum, normalizeCurrency } from "@/lib/money";

const ICONS = { Building2, Zap, Wrench, Paintbrush };

const baseRow = (idx, extra) => ({
  index: idx, home_type: "", house_number: "", block_number: "", blockError: "",
  description: "", piece_count: "", quantity: "", unit: "", unit_price: "", total_price: "",
  ...extra,
});

const Label = ({ children }) => (
  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{children}</label>
);
const inputCls =
  "h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-[var(--brand)] disabled:bg-slate-50 disabled:text-slate-500";

export default function EstimationForm({ category, unitNumber, editId }) {
  const config = useMemo(() => getCategoryConfig(category), [category]);
  const isEditMode = !!editId;
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const isKU = language === "ku";
  const navigate = useNavigate();
  const accent = config.accent;
  const TitleIcon = ICONS[config.icon] || Building2;

  const [estNum, setEstNum] = useState("");
  const [estNumError, setEstNumError] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [subject, setSubject] = useState("");
  const [contractorName, setContractorName] = useState("");
  const [contractorPhone, setContractorPhone] = useState("");
  const [previousRetention, setPreviousRetention] = useState("");
  const [retentionPct, setRetentionPct] = useState(10);
  const [currency, setCurrency] = useState("IQD");
  const [rows, setRows] = useState([baseRow(1, config.rowFields)]);
  const [templateConfig, setTemplateConfig] = useState({});
  const [showAddRowDialog, setShowAddRowDialog] = useState(false);

  const [step, setStep] = useState("form");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [excelUrl, setExcelUrl] = useState("");
  const [done, setDone] = useState(false);

  const topLevelContractor = config.contractorMode === "top_level";

  useEffect(() => {
    if (config.usesTemplateConfig) {
      apiClient.get(config.configEndpoint).then(setTemplateConfig).catch(() => {});
    }
  }, [config]);

  useEffect(() => {
    if (isEditMode) {
      apiClient
        .get(`/estimations/${editId}`)
        .then((est) => {
          setEstNum(String(est.estimation_number || ""));
          setDate(est.date || new Date().toISOString().split("T")[0]);
          if (est.subject) setSubject(est.subject);
          if (est.contractor_name) setContractorName(est.contractor_name);
          if (est.contractor_phone) setContractorPhone(est.contractor_phone);
          if (est.retention_pct !== undefined && est.retention_pct !== null) setRetentionPct(est.retention_pct);
          if (est.currency) setCurrency(normalizeCurrency(est.currency));
          if (est.previous_retention) setPreviousRetention(String(est.previous_retention));
          if (est.rows && est.rows.length > 0)
            setRows(est.rows.map((r, i) => ({ ...baseRow(i + 1, config.rowFields), ...r, index: i + 1 })));
        })
        .catch(() => {});
    }
  }, [editId]);

  // Template-driven field visibility (structure & finishing).
  const firstWorkType = rows.find((r) => r.main_type)?.main_type || "";
  const wtConfig = config.usesTemplateConfig && firstWorkType ? templateConfig[firstWorkType] : undefined;
  const retentionEnabled = config.usesTemplateConfig ? (wtConfig ? wtConfig.retention_enabled !== false : true) : true;
  const hiddenFields = wtConfig?.hidden_fields || [];
  const showUnit = !hiddenFields.includes("unit");
  const showQuantity = !hiddenFields.includes("quantity");
  const showPieceCount = !hiddenFields.includes("piece_count");
  const showHouseNumber = !hiddenFields.includes("house_number");
  const showContractorPhoneRow = wtConfig?.has_contractor_fields || false;
  const manualBlockNumber = wtConfig?.custom_fields?.manual_block_number || false;
  const hasWorkPlace = wtConfig?.custom_fields?.has_work_place || false;

  const subtotal = cleanNum(rows.reduce((s, r) => s + (parseFloat(r.total_price) || 0), 0));

  const dropdownFields = config.dropdowns.map((d) => d.field);

  const updateRow = (i, field, value) => {
    setRows((prev) => {
      const u = [...prev];
      u[i] = { ...u[i], [field]: value };
      if (field === "home_type" || field === "house_number") {
        const ht = field === "home_type" ? value : u[i].home_type;
        const hn = field === "house_number" ? value : u[i].house_number;
        const block = getBlockNumber(ht, hn);
        u[i].block_number = block || "";
        u[i].blockError = ht && hn && !block ? t("invalid_house_number") : "";
      }
      // Cascading reset: clear later dropdown levels when an earlier one changes.
      const ddIdx = dropdownFields.indexOf(field);
      if (ddIdx >= 0) {
        for (let j = ddIdx + 1; j < dropdownFields.length; j++) u[i][dropdownFields[j]] = "";
      }
      if (field === "piece_count" || field === "quantity" || field === "unit_price") {
        const pc = parseFloat(field === "piece_count" ? value : u[i].piece_count) || 0;
        const qty = parseFloat(field === "quantity" ? value : u[i].quantity) || 0;
        const up = parseFloat(field === "unit_price" ? value : u[i].unit_price) || 0;
        let total;
        if (pc > 0 && qty > 0) total = pc * qty * up;
        else if (pc > 0) total = pc * up;
        else total = qty * up;
        u[i].total_price = total ? String(cleanNum(total)) : "";
      }
      return u;
    });
  };

  const addEmptyRow = () => {
    setShowAddRowDialog(false);
    setRows((p) => [...p, baseRow(p.length + 1, config.rowFields)]);
  };
  const addCopyRow = () => {
    setShowAddRowDialog(false);
    setRows((p) => {
      const last = p[p.length - 1];
      // Deep copy so the new row is fully independent from the original.
      const copy = { ...baseRow(p.length + 1, config.rowFields), ...JSON.parse(JSON.stringify(last)) };
      copy.index = p.length + 1;
      return [...p, copy];
    });
  };
  const addRow = () => setShowAddRowDialog(true);
  const removeRow = (i) => {
    if (rows.length <= 1) return;
    setRows((p) => p.filter((_, j) => j !== i).map((r, j) => ({ ...r, index: j + 1 })));
  };

  const buildRowPayload = () =>
    rows.map((r) => ({
      index: r.index, home_type: r.home_type, house_number: r.house_number, block_number: r.block_number,
      description: r.description, piece_count: r.piece_count, quantity: r.quantity,
      unit: r.unit, unit_price: r.unit_price, total_price: r.total_price,
      ...config.buildRowExtra(r),
    }));

  const resolvedContractorName = topLevelContractor
    ? contractorName
    : rows.find((r) => r.contractor_name)?.contractor_name || "";

  const resolveUnitNum = () => {
    const fr = rows[0];
    return fr?.home_type && fr?.house_number ? `${fr.home_type}-${fr.house_number}` : unitNumber || "";
  };

  const validateForm = async () => {
    setError("");
    setEstNumError("");
    const numVal = parseInt(estNum, 10);
    if (!estNum || isNaN(numVal) || numVal <= 0) {
      setEstNumError(t("estimation_number_required"));
      return false;
    }
    if (!subject.trim()) {
      setError(`${t("subject")} ${t("is_required")}`);
      return false;
    }
    if (!rows.some((r) => r[config.primaryField] || r.description.trim())) {
      setError(t("add_at_least_one_row"));
      return false;
    }
    if (rows.find((r) => r.home_type && r.house_number && !r.block_number)) {
      setError(t("invalid_house_number"));
      return false;
    }
    if (!isEditMode) {
      try {
        const check = await apiClient.get(
          `/estimations/count?unit_number=${resolveUnitNum()}&category=${category}&estimation_number=${numVal}`
        );
        if (check.is_valid === false) {
          setEstNumError(check.error || t("estimation_number_duplicate"));
          return false;
        }
      } catch {
        /* proceed */
      }
    }
    return true;
  };

  const handlePreview = async () => {
    if (!(await validateForm())) return;
    setPreviewLoading(true);
    setError("");
    try {
      const res = await apiClient.post("/estimations/preview-excel", {
        category,
        estimation_number: parseInt(estNum, 10),
        date,
        subject,
        engineer_name: user?.name || "",
        contractor_name: resolvedContractorName,
        contractor_phone: contractorPhone,
        previous_retention: parseFloat(previousRetention) || 0,
        retention_pct: retentionPct,
        currency,
        rows: buildRowPayload(),
      });
      if (res.preview_url) {
        setPreviewUrl(`${backendUrl()}${res.preview_url}`);
        setStep("previewing");
      }
    } catch (e) {
      setError(e.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        category,
        date,
        subject,
        estimation_number: parseInt(estNum, 10),
        rows: buildRowPayload(),
        subtotal,
        additional_costs: 0,
        discount: 0,
        final_total: subtotal,
        previous_retention: parseFloat(previousRetention) || 0,
        retention_pct: retentionPct,
        currency,
      };
      if (topLevelContractor) {
        payload.contractor_name = contractorName;
        payload.contractor_phone = contractorPhone;
      }
      let savedId;
      if (isEditMode) {
        await apiClient.put(`/estimations/${editId}`, { data: payload });
        savedId = editId;
      } else {
        const created = await apiClient.post("/estimations", { unit_number: resolveUnitNum(), data: payload });
        savedId = created.id;
      }
      try {
        const docRes = await apiClient.post(`/estimations/${savedId}/generate-documents`, {});
        if (docRes.excel_url) setExcelUrl(docRes.excel_url);
      } catch {
        /* doc gen best-effort */
      }
      setStep("confirmed");
      setDone(true);
    } catch (e) {
      setError(e.message || t("failed_submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const finish = () => navigate(-1);

  const currentEst = cleanNum(subtotal * (1 - retentionPct / 100));
  const currentRet = cleanNum(subtotal * (retentionPct / 100));
  const finalSummary = cleanNum(currentEst + (parseFloat(previousRetention) || 0));
  const disabled = step !== "form";

  return (
    <div className="mx-auto max-w-5xl pb-10">
      {/* Title */}
      <div className="mb-5 flex items-center gap-3 rounded-2xl border p-5" style={{ backgroundColor: `${accent}0d`, borderColor: `${accent}33` }}>
        <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: `${accent}20` }}>
          <TitleIcon className="h-6 w-6" style={{ color: accent }} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">{t(config.titleKey)}</h1>
          {unitNumber ? <p className="text-sm text-slate-500">{t("unit")} {unitNumber}</p> : null}
        </div>
      </div>

      {/* Preview banner */}
      {step === "previewing" ? (
        <div className="mb-5 rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-5">
          <div className="mb-3 flex items-center gap-3">
            <FileText className="h-6 w-6 text-emerald-600" />
            <div>
              <div className="text-base font-bold text-emerald-700">{t("preview_ready")}</div>
              <div className="text-xs text-emerald-600">{t("preview_note")}</div>
            </div>
          </div>
          <a href={previewUrl} target="_blank" rel="noreferrer" data-testid="download-preview-btn" className="mb-3 flex items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white">
            <Download className="h-4 w-4" /> {t("download_excel")}
          </a>
          <div className="flex gap-3">
            <button onClick={() => { setStep("form"); setPreviewUrl(""); }} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border-2 py-3 text-sm font-semibold" style={{ borderColor: accent, color: accent }}>
              <ArrowLeft className="h-4 w-4" /> {t("back_to_edit")}
            </button>
            <button data-testid="confirm-submit-btn" onClick={handleConfirmSubmit} disabled={submitting} className="flex flex-[2] items-center justify-center gap-1.5 rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-70" style={{ backgroundColor: accent }}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t("confirm_and_submit")}
            </button>
          </div>
        </div>
      ) : null}

      {/* Success */}
      {done ? (
        <div className="mb-5 flex flex-col items-center rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-8 text-center">
          <CheckCircle className="mb-3 h-14 w-14 text-emerald-500" />
          <div className="text-lg font-bold text-slate-900">{t("success")}</div>
          <div className="mt-1 text-sm text-slate-600">{t("estimation_saved")}</div>
          <div className="mt-5 flex gap-3">
            {excelUrl ? (
              <a href={excelUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white" style={{ backgroundColor: accent }}>
                <Download className="h-4 w-4" /> {t("download_excel")}
              </a>
            ) : null}
            <button onClick={finish} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600">{t("ok")}</button>
          </div>
        </div>
      ) : null}

      {!done ? (
        <>
          {/* Top info */}
          <div className={`mb-5 rounded-2xl border border-slate-200 bg-white p-6 ${disabled ? "opacity-70" : ""}`}>
            <div className="mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5" style={{ color: accent }} />
              <h3 className="text-base font-semibold text-slate-900">{t("top_info")}</h3>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>{t("estimation_number")}</Label>
                <input data-testid="estimation-number-input" className={inputCls} style={estNumError ? { borderColor: "#DC2626" } : {}} value={estNum} onChange={(e) => { setEstNum(e.target.value.replace(/[^0-9]/g, "")); setEstNumError(""); }} placeholder={t("enter_estimation_number")} inputMode="numeric" disabled={disabled} />
                {estNumError ? <p className="mt-1 text-xs text-red-600">{estNumError}</p> : null}
              </div>
              <div>
                <Label>{t("subject")}</Label>
                <input data-testid="subject-input" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("enter_subject")} disabled={disabled} />
              </div>
              <div>
                <Label>{t("date_label")}</Label>
                <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} disabled={disabled} />
              </div>
              <div>
                <Label>{t("currency")}</Label>
                <select data-testid="currency-select" className={inputCls} value={currency} onChange={(e) => setCurrency(e.target.value)} disabled={disabled}>
                  {CURRENCY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.code}>{t(c.code === "USD" ? "currency_usd" : "currency_iqd")} ({c.symbol})</option>
                  ))}
                </select>
              </div>
              {topLevelContractor ? (
                <>
                  <div>
                    <Label>{t("contractor_name")}</Label>
                    <input className={inputCls} value={contractorName} onChange={(e) => setContractorName(e.target.value)} placeholder={t("enter_contractor_name")} disabled={disabled} />
                  </div>
                  <div>
                    <Label>{t("contractor_phone")}</Label>
                    <input className={inputCls} value={contractorPhone} onChange={(e) => setContractorPhone(e.target.value)} placeholder={t("enter_phone")} disabled={disabled} />
                  </div>
                </>
              ) : null}
              <div>
                <Label>{t("engineer_name_label")}</Label>
                <div className="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-100 px-3 text-sm text-slate-700">{user?.name || "-"}</div>
              </div>
              <div>
                <Label>{t("category_label")}</Label>
                <div className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-slate-100 px-3 text-sm text-slate-700">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: accent }} />
                  {t(config.categoryWorkKey)}
                </div>
              </div>
            </div>
          </div>

          {/* Table entries */}
          <div className={`mb-5 rounded-2xl border border-slate-200 bg-white p-6 ${disabled ? "opacity-70" : ""}`}>
            <div className="mb-4 flex items-center gap-2">
              <Grid3x3 className="h-5 w-5" style={{ color: accent }} />
              <h3 className="flex-1 text-base font-semibold text-slate-900">{t("table_entries")}</h3>
              <span className="rounded-lg px-2.5 py-1 text-xs font-semibold" style={{ backgroundColor: `${accent}15`, color: accent }}>{rows.length} {t("rows_label")}</span>
            </div>

            <div className="space-y-4">
              {rows.map((row, idx) => (
                <div key={idx} data-testid={`work-row-${idx}`} className={`rounded-xl border border-slate-200 p-4 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                  <div className="mb-3 flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: accent }}>{row.index}</span>
                    <span className="flex-1 text-sm font-semibold text-slate-800">{t("row_label")} {row.index}</span>
                    {rows.length > 1 && step === "form" ? (
                      <button data-testid={`remove-row-${idx}`} onClick={() => removeRow(idx)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {showHouseNumber ? (
                      <div>
                        <Label>{t("home_type")}</Label>
                        <div className="flex gap-2">
                          {HOME_TYPES.map((ht) => (
                            <button key={ht} type="button" disabled={disabled} onClick={() => updateRow(idx, "home_type", ht)} className={`flex-1 rounded-lg border py-2 text-sm font-semibold ${row.home_type === ht ? "border-transparent text-white" : "border-slate-200 bg-white text-slate-500"}`} style={row.home_type === ht ? { backgroundColor: accent } : {}}>
                              {ht}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {showHouseNumber ? (
                      <div>
                        <Label>{t("house_number")}</Label>
                        <input className={inputCls} value={row.house_number} onChange={(e) => updateRow(idx, "house_number", e.target.value)} placeholder={t("enter_house_number")} disabled={disabled} />
                      </div>
                    ) : null}

                    <div>
                      <Label>{t("block_number")}</Label>
                      {manualBlockNumber ? (
                        <input className={inputCls} value={row.block_number} onChange={(e) => updateRow(idx, "block_number", e.target.value)} placeholder={t("enter_block_number")} disabled={disabled} />
                      ) : (
                        <div className="flex h-10 items-center gap-2 rounded-lg border bg-slate-100 px-3 text-sm" style={{ borderColor: row.block_number ? "#059669" : row.blockError ? "#DC2626" : "#E2E8F0" }}>
                          {row.block_number ? (
                            <><MapPin className="h-4 w-4 text-emerald-600" /><span className="font-semibold text-emerald-600">{row.block_number}</span></>
                          ) : row.blockError ? (
                            <><AlertCircle className="h-4 w-4 text-red-600" /><span className="text-xs text-red-600">{row.blockError}</span></>
                          ) : (
                            <span className="text-slate-400">{t("auto_calculated")}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {hasWorkPlace ? (
                      <div>
                        <Label>{t("work_place")}</Label>
                        <div className="flex gap-2">
                          {config.workPlaceOptions.map((wp) => (
                            <button key={wp} type="button" disabled={disabled} onClick={() => updateRow(idx, "work_place", wp)} className={`flex-1 rounded-lg border py-2 text-sm font-semibold ${row.work_place === wp ? "border-transparent text-white" : "border-slate-200 bg-white text-slate-500"}`} style={row.work_place === wp ? { backgroundColor: accent } : {}}>
                              {wp}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {config.contractorMode === "per_row" ? (
                      <div>
                        <Label>{t("contractor_name")}</Label>
                        <input className={inputCls} value={row.contractor_name} onChange={(e) => updateRow(idx, "contractor_name", e.target.value)} placeholder={t("enter_contractor_name")} disabled={disabled} />
                      </div>
                    ) : null}
                    {config.contractorMode === "per_row" && showContractorPhoneRow && idx === 0 ? (
                      <div>
                        <Label>{t("contractor_phone")}</Label>
                        <input className={inputCls} value={contractorPhone} onChange={(e) => setContractorPhone(e.target.value)} placeholder={t("enter_phone")} disabled={disabled} />
                      </div>
                    ) : null}

                    {/* Cascading work-type dropdowns */}
                    {config.dropdowns.map((dd) => {
                      if (!dd.visible(row)) return null;
                      const { keys, labels } = dd.options(row, language);
                      return (
                        <div key={dd.field}>
                          <Label>{t(dd.labelKey)}</Label>
                          <select data-testid={`row-${idx}-${dd.field}`} className={inputCls} value={row[dd.field] || ""} onChange={(e) => updateRow(idx, dd.field, e.target.value)} disabled={disabled}>
                            <option value="">{t(dd.placeholderKey)}</option>
                            {keys.map((k, i) => (
                              <option key={k} value={k}>{labels[i]}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })}

                    <div className="sm:col-span-2">
                      <Label>{t("description")}</Label>
                      <textarea rows={2} className={`${inputCls} h-auto py-2`} value={row.description} onChange={(e) => updateRow(idx, "description", e.target.value)} placeholder={t("enter_description")} disabled={disabled} />
                    </div>

                    {showPieceCount ? (
                      <div>
                        <Label>{t("piece_count")}</Label>
                        <input className={inputCls} value={row.piece_count} onChange={(e) => updateRow(idx, "piece_count", e.target.value)} placeholder="0" inputMode="numeric" disabled={disabled} />
                      </div>
                    ) : null}
                    {showQuantity ? (
                      <div>
                        <Label>{t("quantity")}</Label>
                        <input className={inputCls} value={row.quantity} onChange={(e) => updateRow(idx, "quantity", e.target.value)} placeholder="0" inputMode="numeric" disabled={disabled} />
                      </div>
                    ) : null}
                    {showUnit ? (
                      <div>
                        <Label>{t("unit_label")}</Label>
                        <select className={inputCls} value={row.unit || ""} onChange={(e) => updateRow(idx, "unit", e.target.value)} disabled={disabled}>
                          <option value="">{t("select_unit")}</option>
                          {UNIT_OPTIONS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    <div>
                      <Label>{t("unit_price")}</Label>
                      <input className={inputCls} value={row.unit_price} onChange={(e) => updateRow(idx, "unit_price", e.target.value)} placeholder="0.00" inputMode="decimal" disabled={disabled} />
                    </div>
                    <div>
                      <Label>{t("total_price")}</Label>
                      <div className="flex h-10 items-center rounded-lg bg-slate-100 px-3 text-sm font-semibold text-slate-900">
                        {fmtMoney(row.total_price || 0, currency)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {step === "form" ? (
              <button data-testid="add-row-btn" onClick={addRow} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 text-sm font-semibold" style={{ borderColor: `${accent}55`, color: accent, backgroundColor: `${accent}0a` }}>
                <Plus className="h-4 w-4" /> {t("add_row")}
              </button>
            ) : null}
          </div>

          {/* Totals */}
          <div className={`mb-5 rounded-2xl border border-slate-200 bg-white p-6 ${disabled ? "opacity-70" : ""}`}>
            <div className="mb-4 flex items-center gap-2">
              <Calculator className="h-5 w-5" style={{ color: accent }} />
              <h3 className="text-base font-semibold text-slate-900">{t("totals_section")}</h3>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-500">{t("total_work")}</span>
              <span className="text-base font-semibold text-slate-900">{fmtMoney(subtotal, currency)}</span>
            </div>
            {retentionEnabled ? (
              <>
                <div className="my-2 h-px bg-slate-100" />
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-500">{t("retention_percentage")}</span>
                  <select data-testid="retention-select" className="rounded-lg border border-slate-200 bg-[var(--brand-light)] px-3 py-1.5 text-sm font-semibold" style={{ color: accent }} value={retentionPct} onChange={(e) => setRetentionPct(Number(e.target.value))} disabled={disabled}>
                    {RETENTION_OPTIONS.map((p) => (
                      <option key={p} value={p}>{p}%</option>
                    ))}
                  </select>
                </div>
                <div className="my-2 h-px bg-slate-100" />
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-500">{isKU ? `پێشینە ئێستا (${100 - retentionPct}%)` : `Current Estimation (${100 - retentionPct}%)`}</span>
                  <span className="text-base font-semibold text-emerald-600">{fmtMoney(currentEst, currency)}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-500">{isKU ? `تەئمینات ڤێلا (${retentionPct}%)` : `Current Retention (${retentionPct}%)`}</span>
                  <span className="text-base font-semibold text-red-500">{fmtMoney(currentRet, currency)}</span>
                </div>
                <div className="my-2 h-px bg-slate-100" />
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-500">{t("previous_retention_10")}</span>
                  <input className="h-9 w-36 rounded-lg border border-slate-200 bg-slate-50 px-3 text-end text-sm" value={previousRetention} onChange={(e) => setPreviousRetention(e.target.value)} placeholder="0" inputMode="numeric" disabled={disabled} />
                </div>
                <div className="my-2 h-px bg-slate-100" />
                <div className="flex items-center justify-between py-2">
                  <span className="text-base font-bold text-slate-900">{t("final_summary")}</span>
                  <span className="text-xl font-bold text-emerald-600">{fmtMoney(finalSummary, currency)}</span>
                </div>
              </>
            ) : null}
          </div>

          {error ? (
            <div className="mb-4 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          ) : null}

          {step === "form" ? (
            <button data-testid="preview-estimation-btn" onClick={handlePreview} disabled={previewLoading} className="flex w-full items-center justify-center gap-2 rounded-xl border-2 py-4 text-base font-semibold disabled:opacity-70" style={{ borderColor: accent, color: accent, backgroundColor: `${accent}0a` }}>
              {previewLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Eye className="h-5 w-5" />} {t("preview_estimation")}
            </button>
          ) : null}
        </>
      ) : null}

      {/* Add Row choice dialog */}
      {showAddRowDialog ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowAddRowDialog(false)}>
          <div data-testid="add-row-dialog" className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 flex items-center gap-2">
              <Plus className="h-5 w-5" style={{ color: accent }} />
              <h3 className="text-base font-bold text-slate-900">{t("add_row")}</h3>
            </div>
            <p className="mb-5 text-sm text-slate-500">{t("add_row_question")}</p>
            <div className="flex flex-col gap-3">
              <button data-testid="add-row-empty-btn" onClick={addEmptyRow} className="flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-semibold" style={{ borderColor: accent, color: accent }}>
                <FileText className="h-4 w-4" /> {t("add_row_empty")}
              </button>
              <button data-testid="add-row-copy-btn" onClick={addCopyRow} className="flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white" style={{ backgroundColor: accent }}>
                <CheckCircle2 className="h-4 w-4" /> {t("add_row_copy")}
              </button>
              <button data-testid="add-row-cancel-btn" onClick={() => setShowAddRowDialog(false)} className="rounded-xl py-2.5 text-sm font-semibold text-slate-500">
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
